/**
 * WCAG 2.0 contrast utilities for per-BA branding. Ported from linxapp's
 * brandingContrast.ts (which wraps MUI getContrastRatio); reimplemented here
 * because this app has no MUI. Pure functions, no Angular dependency.
 */

/** Accepts 3- or 6-digit hex (`#abc` / `#aabbcc`); returns `[r,g,b]` or null. */
export const hexToRgbTuple = (hex: string): [number, number, number] | null => {
  let clean = hex.trim().replace(/^#/, '');
  if (/^[\da-f]{3}$/i.test(clean)) {
    clean = clean
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[\da-f]{6}$/i.test(clean)) {
    return null;
  }
  return [parseInt(clean.slice(0, 2), 16), parseInt(clean.slice(2, 4), 16), parseInt(clean.slice(4, 6), 16)];
};

const channelLuminance = (channel: number): number => {
  const s = channel / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

/**
 * WCAG relative luminance of an exact (possibly fractional) RGB tuple. Callers doing numeric
 * search need this un-rounded form: rounding to 8-bit channels mid-search turns a monotonic
 * function into a step function and costs precision.
 */
export const relativeLuminanceRgb = ([r, g, b]: [number, number, number]): number =>
  0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);

/** WCAG relative luminance; NaN for unparseable input. */
export const relativeLuminance = (hex: string): number => {
  const rgb = hexToRgbTuple(hex);
  if (!rgb) {
    return NaN;
  }
  return relativeLuminanceRgb(rgb);
};

/** WCAG contrast ratio (1..21); NaN if either color is unparseable. */
export const contrastRatio = (a: string, b: string): number => {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  if (Number.isNaN(la) || Number.isNaN(lb)) {
    return NaN;
  }
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Inverse of `contrastRatio`: the relative luminance a colour must sit at or BELOW to
 * clear `minRatio` against the lighter surface `bg`. Against white at 1.5:1 that is 0.65.
 * NaN when `bg` is unparseable. Assumes `bg` is the lighter of the pair (the white card).
 */
export const luminanceForContrastOn = (bg: string, minRatio: number): number => {
  const bgLuminance = relativeLuminance(bg);
  if (Number.isNaN(bgLuminance)) {
    return NaN;
  }
  return (bgLuminance + 0.05) / minRatio - 0.05;
};

/** Light or dark foreground to render ON TOP of `bg`. Dark on parse failure. */
export const readableTextColor = (bg: string, light = '#ffffff', dark = '#111111'): string => {
  const lightRatio = contrastRatio(light, bg);
  const darkRatio = contrastRatio(dark, bg);
  if (Number.isNaN(lightRatio) || Number.isNaN(darkRatio)) {
    return dark;
  }
  return lightRatio >= darkRatio ? light : dark;
};

/** Use `color` as text/accent on `bg` only when contrast >= minRatio, else `fallback`. */
export const ensureReadableOn = (color: string, bg = '#ffffff', fallback = '#111111', minRatio = 1.5): string => {
  const ratio = contrastRatio(color, bg);
  if (Number.isNaN(ratio)) {
    return fallback;
  }
  return ratio >= minRatio ? color : fallback;
};

/** True when `color` is visually distinguishable from `bg` (>= minRatio). */
export const hasSufficientContrast = (color: string, bg: string, minRatio = 1.5): boolean => {
  const ratio = contrastRatio(color, bg);
  return !Number.isNaN(ratio) && ratio >= minRatio;
};
